/**
 * Keyboard shortcut labels that read the way the reader's own keyboard is printed.
 *
 * `mod` is the primary modifier: Command on macOS, Control everywhere else. Matching
 * follows the same rule as the editor's existing Cmd+S/Cmd+Z handler
 * (EditorV2/hooks/useKeyboardShortcuts.ts), so a shortcut is never shown as ⌘ but
 * answered on Ctrl, or the other way round.
 */
export type ShortcutKey = 'mod' | 'alt' | 'shift' | 'enter' | 'escape' | (string & {});

const MAC_LABELS: Record<string, string> = { mod: '⌘', alt: '⌥', shift: '⇧', enter: '↵', escape: 'Esc' };
const OTHER_LABELS: Record<string, string> = { mod: 'Ctrl', alt: 'Alt', shift: 'Shift', enter: 'Enter', escape: 'Esc' };

// aria-keyshortcuts names keys by their KeyboardEvent.key values, not by the glyphs.
const ARIA_NAMES: Record<string, string> = { alt: 'Alt', shift: 'Shift', enter: 'Enter', escape: 'Escape' };

/** Same test the editor's other shortcuts use, so the two can never disagree. */
export const isMacPlatform = (platform: string) => platform.toUpperCase().includes('MAC');

export const keyLabel = (key: ShortcutKey, isMac: boolean) =>
  (isMac ? MAC_LABELS : OTHER_LABELS)[key] ?? key.toUpperCase();

/** "⇧⌥F" on macOS, where the glyphs run together as printed on menus; "Shift+Alt+F" elsewhere. */
export const formatShortcut = (keys: readonly ShortcutKey[], isMac: boolean) =>
  keys.map((key) => keyLabel(key, isMac)).join(isMac ? '' : '+');

/** The value for aria-keyshortcuts, e.g. "Meta+Enter" or "Control+Enter". */
export const ariaShortcut = (keys: readonly ShortcutKey[], isMac: boolean) =>
  keys.map((key) => (key === 'mod' ? (isMac ? 'Meta' : 'Control') : (ARIA_NAMES[key] ?? key.toUpperCase()))).join('+');

/** Whether the event carries the primary modifier for this platform. */
export const hasModKey = (ev: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey'>, isMac: boolean) =>
  isMac ? ev.metaKey : ev.ctrlKey;

const detectMac = () => (typeof navigator === 'undefined' ? false : isMacPlatform(navigator.platform ?? ''));

/**
 * The platform's shortcut helpers. A hook in name so callers read like the rest of the
 * UI layer; the platform cannot change while the page is open, so it holds no state.
 */
export function usePlatformKeys() {
  const isMac = detectMac();
  return {
    isMac,
    label: (keys: readonly ShortcutKey[]) => formatShortcut(keys, isMac),
    aria: (keys: readonly ShortcutKey[]) => ariaShortcut(keys, isMac),
    hasMod: (ev: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey'>) => hasModKey(ev, isMac),
  };
}

export type PlatformKeys = ReturnType<typeof usePlatformKeys>;
